import { useTranslation } from "@shared/utils/internationalization";
import { useCallback } from "react";
import { PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import Text from "@ui-kit/typography/Text/Text.web";
import { PURCHASE_SOURCE_PLATFORM_MAP } from "@shared/constants/enums";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import { getEmbeddedPurchasePlatform } from "../../utils/purchase-platform-utils";
import {
  displayAllInPricingComponent,
  isExposedPricingInfo,
  isNestedPricingInfo,
} from "../../utils/price-utils";
import type { AllInPricingSharedProps } from "./AllInPricing.props";

export default function AllInPricing(props: AllInPricingProps) {
  const {
    feesPricingInfo,
    currency,
    price = 0,
    hasAllInPricing,
    autoDiscountAmount = 0,
    paymentType = PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
    purchaseSourcePlatform = getEmbeddedPurchasePlatform() ??
      PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
  } = props;
  const { t } = useTranslation("purchases");

  const getAllInPricingText = useCallback(() => {
    let returnElement = <></>;
    let selectedPricingInfo;

    const discount = autoDiscountAmount ?? 0;

    // check for typing from non-calendar endpoints
    if (
      paymentType &&
      isNestedPricingInfo(feesPricingInfo, purchaseSourcePlatform, paymentType)
    ) {
      selectedPricingInfo =
        feesPricingInfo[purchaseSourcePlatform][paymentType];
    }
    // check for typing from calendar details endpoint
    else if (feesPricingInfo && isExposedPricingInfo(feesPricingInfo)) {
      selectedPricingInfo = feesPricingInfo;
    }

    // Case #1: Both taxes and fees > 0 - show fee amount, fee text and plus taxes text
    if (
      selectedPricingInfo &&
      parseFloat(selectedPricingInfo?.taxes) > 0 &&
      parseFloat(selectedPricingInfo?.service_charges) > 0
    ) {
      returnElement = (
        <>
          <CurrencyFormat
            value={Number(price) - Number(discount)}
            displayType="text"
            currency={currency}
            includeSuffix
          />
          {` + `}
          <CurrencyFormat
            value={selectedPricingInfo?.service_charges.toString()}
            displayType="text"
            currency={currency}
          />
          {` ${t("Fees")} + ${t("Applicable taxes at checkout")}`}
        </>
      );
    }
    // Case #2: Taxes absorbed - only show fees amount and fees text
    else if (
      selectedPricingInfo &&
      parseFloat(selectedPricingInfo?.taxes) <= 0 &&
      parseFloat(selectedPricingInfo?.service_charges) > 0
    ) {
      returnElement = (
        <>
          <CurrencyFormat
            value={Number(price) - Number(discount)}
            displayType="text"
            currency={currency}
            includeSuffix
          />
          {` + `}
          <CurrencyFormat
            value={selectedPricingInfo?.service_charges.toString()}
            displayType="text"
            currency={currency}
          />
          {` ${t("Fees")}`}
        </>
      );
    }
    // Case #3: Fees absorbed - only show plus taxes text
    else if (
      selectedPricingInfo &&
      parseFloat(selectedPricingInfo?.service_charges) <= 0 &&
      parseFloat(selectedPricingInfo?.taxes) > 0
    ) {
      returnElement = (
        <>
          <CurrencyFormat
            value={(
              parseFloat(selectedPricingInfo?.total_price_no_tax) - discount
            ).toString()}
            displayType="text"
            currency={currency}
            includeSuffix
          />
          {` + ${t("Applicable taxes at checkout")}`}
        </>
      );
    }
    return returnElement;
  }, [
    purchaseSourcePlatform,
    paymentType,
    currency,
    feesPricingInfo,
    price,
    t,
  ]);

  return displayAllInPricingComponent(
    price?.toString(),
    hasAllInPricing,
    feesPricingInfo,
    paymentType,
    purchaseSourcePlatform
  ) ? (
    <Text
      data-testid="all-in-pricing-web"
      fontSize="sm"
      color="gray.700"
      fontWeight={700}
    >
      {getAllInPricingText()}
    </Text>
  ) : (
    <></>
  );
}

export type AllInPricingProps = AllInPricingSharedProps & {
  // add any web-specific props here
};
